import { useId } from "react"
import { useI18n } from "../../i18n"
import type { TranslationKey } from "../../i18n/types"
import type { BuiltSchedule, CadenceMode, CadenceState } from "./cadence"

// C7: the schedule picker (ADR-017:130). Every day / Weekdays / Weekends /
// Specific days → time, plus Every N minutes/hours, Once at, and an Advanced
// raw field. Controlled: all state lives in SchedulesPanel, all conversion in
// cadence.ts. This file only renders.

const MODES: readonly { mode: CadenceMode; key: TranslationKey }[] = [
  { mode: "daily", key: "settings.schedules.cadence.daily" },
  { mode: "weekdays", key: "settings.schedules.cadence.weekdays" },
  { mode: "weekends", key: "settings.schedules.cadence.weekends" },
  { mode: "specific", key: "settings.schedules.cadence.specific" },
  { mode: "interval", key: "settings.schedules.cadence.interval" },
  { mode: "once", key: "settings.schedules.cadence.once" },
  { mode: "advanced", key: "settings.schedules.cadence.advanced" },
]

const DAY_KEYS: readonly TranslationKey[] = [
  "settings.schedules.cadence.day.0",
  "settings.schedules.cadence.day.1",
  "settings.schedules.cadence.day.2",
  "settings.schedules.cadence.day.3",
  "settings.schedules.cadence.day.4",
  "settings.schedules.cadence.day.5",
  "settings.schedules.cadence.day.6",
]

export interface CadencePickerProps {
  value: CadenceState
  onChange: (next: CadenceState) => void
  built: BuiltSchedule
}

export function CadencePicker({ value, onChange, built }: CadencePickerProps) {
  const { t } = useI18n()
  const modeId = useId()
  const timeId = useId()
  const rawId = useId()

  const set = (patch: Partial<CadenceState>) => onChange({ ...value, ...patch })
  const isCron = value.mode === "daily" || value.mode === "weekdays" || value.mode === "weekends" || value.mode === "specific"

  function toggleDay(day: number) {
    const days = value.days.includes(day) ? value.days.filter((d) => d !== day) : [...value.days, day]
    set({ days })
  }

  return (
    <div className="settings-schedule-cadence">
      <label className="settings-row-label" htmlFor={modeId}>
        {t("settings.schedules.when")}
      </label>
      <select
        id={modeId}
        className="settings-select"
        value={value.mode}
        onChange={(e) => set({ mode: e.target.value as CadenceMode })}
      >
        {MODES.map((m) => (
          <option key={m.mode} value={m.mode}>
            {t(m.key)}
          </option>
        ))}
      </select>

      {isCron && (
        <>
          <label className="settings-row-label" htmlFor={timeId}>
            {t("settings.schedules.cadence.time")}
          </label>
          <input
            id={timeId}
            className="settings-inline-input"
            type="time"
            value={value.time}
            onChange={(e) => set({ time: e.target.value })}
          />
        </>
      )}

      {value.mode === "specific" && (
        <div className="settings-schedule-days" role="group" aria-label={t("settings.schedules.cadence.days")}>
          {DAY_KEYS.map((key, day) => (
            <button
              key={key}
              type="button"
              className="settings-schedule-day"
              aria-pressed={value.days.includes(day)}
              onClick={() => toggleDay(day)}
            >
              {t(key)}
            </button>
          ))}
        </div>
      )}

      {value.mode === "interval" && (
        <div className="settings-schedule-interval">
          <span className="settings-row-label">{t("settings.schedules.cadence.every")}</span>
          <input
            className="settings-inline-input"
            type="number"
            min={1}
            value={value.intervalAmount}
            aria-label={t("settings.schedules.cadence.intervalAmount")}
            onChange={(e) => set({ intervalAmount: Number(e.target.value) })}
          />
          <select
            className="settings-select"
            value={value.intervalUnit}
            aria-label={t("settings.schedules.cadence.intervalUnit")}
            onChange={(e) => set({ intervalUnit: e.target.value as "m" | "h" })}
          >
            <option value="m">{t("settings.schedules.cadence.minutes")}</option>
            <option value="h">{t("settings.schedules.cadence.hours")}</option>
          </select>
        </div>
      )}

      {value.mode === "once" && (
        <>
          <label className="settings-row-label" htmlFor={`${timeId}-once`}>
            {t("settings.schedules.cadence.onceAt")}
          </label>
          <input
            id={`${timeId}-once`}
            className="settings-inline-input"
            type="datetime-local"
            value={value.onceAt}
            onChange={(e) => set({ onceAt: e.target.value })}
          />
        </>
      )}

      {value.mode === "advanced" && (
        <>
          <label className="settings-row-label" htmlFor={rawId}>
            {t("settings.schedules.cadence.raw")}
          </label>
          <input
            id={rawId}
            className="settings-inline-input"
            type="text"
            value={value.raw}
            maxLength={200}
            onChange={(e) => set({ raw: e.target.value })}
          />
        </>
      )}

      <div className="settings-schedule-summary" aria-live="polite">
        <span className="settings-row-hint">
          {t("settings.schedules.summary")}: {built.display || "—"}
        </span>
        {built.warning && (
          <div className="settings-schedule-warning" role="note">
            {built.warning}
          </div>
        )}
      </div>
    </div>
  )
}
